import React from "react";
import PropTypes from "prop-types";
import { Reveal, SweepWord } from "../motion";

/**
 * The house convention for a plain string title: sentence case as given, the
 * last word in the accent sweep, a terminal period. Returns null for a
 * one-word title, which would go entirely accent with nothing to contrast.
 */
function sweepLastWord(title, dark) {
	const words = title
		.trim()
		.replace(/[.!?]$/, "")
		.split(/\s+/);
	if (words.length < 2) return null;
	const last = words.pop();
	return (
		<>
			{`${words.join(" ")} `}
			<SweepWord dark={dark}>{`${last}.`}</SweepWord>
		</>
	);
}

/**
 * Section heading (h2) with optional eyebrow and description, revealed on
 * scroll. `sweepLast` applies the sweep convention to a string `title`;
 * pages that compose the title themselves pass children instead. `dark` is
 * for the ink slab.
 */
const SectionTitle = ({
	eyebrow,
	title,
	children,
	description,
	dark = false,
	sweepLast = false,
}) => {
	const eyebrowClass = dark ? "text-pp-on-slab-muted" : "text-pp-muted";
	const titleClass = dark ? "text-pp-on-slab" : "text-pp-ink";
	const descClass = dark ? "text-pp-on-slab-muted" : "text-pp-muted";
	const swept = !children && sweepLast && typeof title === "string" && sweepLastWord(title, dark);

	return (
		<Reveal>
			{eyebrow && (
				<p
					className={`font-mono text-pp-label uppercase mb-5 tracking-pp-label ${eyebrowClass}`}
				>
					{eyebrow}
				</p>
			)}
			<h2
				className={`font-sligoil font-bold tracking-tight text-pp-display-lg ${titleClass}`}
			>
				{swept || children || title}
			</h2>
			{description && (
				<p
					className={`mt-5 text-base sm:text-lg leading-relaxed max-w-2xl whitespace-pre-line ${descClass}`}
				>
					{description}
				</p>
			)}
		</Reveal>
	);
};

SectionTitle.propTypes = {
	eyebrow: PropTypes.string,
	title: PropTypes.string,
	children: PropTypes.node,
	description: PropTypes.string,
	dark: PropTypes.bool,
	/** Apply the sweep-last-word + period convention to a string title. */
	sweepLast: PropTypes.bool,
};

export default SectionTitle;
